type Props = {
  file: Blob;
  type: string;
  filename: string;
};

export const forceFileDownload = ({
  file,
  type,
  filename,
}: Props): Promise<boolean> => {
  return new Promise((resolve) => {
    try {
      // Check if the file is valid
      if (!file || file.size === 0) {
        console.error('Invalid file: file is empty or undefined');
        resolve(false);
        return;
      }

      // Create download element
      const a = document.createElement('a');
      a.style.display = 'none';
      document.body.appendChild(a);

      // Create blob URL
      const blobUrl = window.URL.createObjectURL(new Blob([file], { type }));
      a.href = blobUrl;
      a.setAttribute('download', filename);

      // Add event listeners to detect if download was successful
      let downloadStarted = false;
      let downloadCompleted = false;

      const cleanup = () => {
        try {
          window.URL.revokeObjectURL(blobUrl);
          if (document.body.contains(a)) {
            document.body.removeChild(a);
          }
        } catch (error) {
          console.warn('Error during cleanup:', error);
        }
      };

      const checkDownloadStatus = () => {
        // Check if the download actually started
        // This is a heuristic - we can't directly detect if the browser blocked the download
        // but we can check if the element was clicked and if the blob URL is still valid
        if (!downloadStarted) {
          downloadStarted = true;
          // Give the browser a moment to process the download
          setTimeout(() => {
            try {
              // Try to access the blob URL - if it's been revoked, the download likely succeeded
              const testImg = new Image();
              testImg.onload = () => {
                downloadCompleted = true;
                cleanup();
                resolve(true);
              };
              testImg.onerror = () => {
                // This is expected for non-image files, but the download might still have succeeded
                downloadCompleted = true;
                cleanup();
                resolve(true);
              };
              testImg.src = blobUrl;
            } catch (error) {
              // If we can't access the blob URL, the download likely succeeded
              downloadCompleted = true;
              cleanup();
              resolve(true);
            }
          }, 100);
        }
      };

      // Add click event listener
      a.addEventListener('click', checkDownloadStatus);

      // Trigger the download
      a.click();

      // Fallback: if we can't detect the download status, assume it worked
      // but give the browser some time to process
      setTimeout(() => {
        if (!downloadCompleted) {
          cleanup();
          resolve(true); // Assume success if we can't detect failure
        }
      }, 2000);
    } catch (error) {
      console.error('Error during file download:', error);
      resolve(false);
    }
  });
};

// Alternative method using window.open for browsers that block programmatic downloads
export const forceFileDownloadAlternative = ({
  file,
  type,
  // eslint-disable-next-line no-unused-vars
  filename,
}: Props): Promise<boolean> => {
  return new Promise((resolve) => {
    try {
      const blobUrl = window.URL.createObjectURL(new Blob([file], { type }));

      // Try opening in a new window/tab with filename hint
      const newWindow = window.open(blobUrl, '_blank');

      if (newWindow) {
        // If window opened successfully, the user can manually save
        // Note: filename is not directly usable in window.open, but the blob URL contains the file
        setTimeout(() => {
          window.URL.revokeObjectURL(blobUrl);
          resolve(true);
        }, 1000);
      } else {
        // If popup was blocked, try the original method
        window.URL.revokeObjectURL(blobUrl);
        resolve(false);
      }
    } catch (error) {
      console.error('Error during alternative file download:', error);
      resolve(false);
    }
  });
};
